"use client";

import React from "react";
import { ArrowRight, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 8,
  order: "asc",
  orderby: "menu_order"
};
const wp_query = new WP_Query(wp_query_params);
const staticProducts = [{
  id: 101,
  title: "Rigaku ResQ / CQL Platform",
  category: "Handheld Raman",
  spec: "1064 nm Laser",
  description: "Handheld 1064 nm Raman analyzer for chemical verification and hazardous substance screening.",
  link: "/RamanHandheldAnalyzersPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_platform.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_platform.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/rigaku_resq_platform.webp-wvc-sizes"
}, {
  id: 102,
  title: "CQL Narc-ID",
  category: "Narcotics Identification",
  spec: "Presumptive Testing",
  description: "Field-ready 1064 nm Raman system configured for identification of illicit narcotics and precursor chemicals.",
  link: "/SafetyAndSecurityPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_narc_id.png-wvc-sizes"
}, {
  id: 103,
  title: "CQL Gen-ID",
  category: "General Chemical",
  spec: "Multi-Library",
  description: "Chemical analyzer engineered for rapid incoming raw material identification and unknown material analysis.",
  link: "/RawMaterialIdentificationPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_gen_id.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_gen_id.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_gen_id.png-wvc-sizes"
}, {
  id: 104,
  title: "CQL Max-ID",
  category: "CBRNe & Defense",
  spec: "High Sensitivity",
  description: "Comprehensive library Raman instrument for chemical warfare agents, toxic industrials, and explosive materials.",
  link: "/BorderSecurityAndDefencePage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cql_max_id.png-wvc-sizes"
}, {
  id: 105,
  title: "QuickDetect Trace Narcotics",
  category: "Trace Detection",
  spec: "SERS Technology",
  description: "Automated surface-enhanced Raman scattering modules for ultra-low concentration narcotic residues.",
  link: "/LawEnforcementPublicSafetyPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_narcotics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_narcotics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_narcotics.png-wvc-sizes"
}, {
  id: 106,
  title: "QuickDetect Trace Explosives",
  category: "Trace Detection",
  spec: "SERS Technology",
  description: "Specialized trace detection kits for non-contact verification of energetic compounds and precursors.",
  link: "/SafetyAndSecurityPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_explosives.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_explosives.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/quickdetect_explosives.png-wvc-sizes"
}, {
  id: 107,
  title: "Accessories & Sampling",
  category: "Hardware",
  spec: "Vials & Adaptors",
  description: "Standardized sampling interfaces, optical adaptors, protective cases, and calibration verification standards.",
  link: "/PowderTestingAccessoriesPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/accessories_sampling.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/accessories_sampling.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/accessories_sampling.webp-wvc-sizes"
}, {
  id: 108,
  title: "Software & Support",
  category: "Lifecycle & Service",
  spec: "21 CFR Part 11",
  description: "Data management software, qualification documentation, annual maintenance plans, and user training programs.",
  link: "/QualificationLifecycleServicesPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/software_support.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/software_support.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/software_support.webp-wvc-sizes"
}];
function CatalogGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({
        length: 8
      }).map((_, i) => <div key={i} data-index={i} className="h-96 rounded-xl border border-border bg-muted/40 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center text-card-foreground">
        <p className="text-sm text-muted-foreground">
          Unable to load live catalog products at this moment.
        </p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>;
  }
  if (products && products.length > 0) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((product, i) => <Card key={product.id} data-index={i} className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
            <div>
              <div className="aspect-4/3 w-full overflow-hidden rounded-t-xl bg-muted">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary text-muted-foreground">
                    <Tag className="size-8 stroke-1 opacity-40" />
                  </div>}
              </div>

              <CardHeader className="space-y-2 pt-4">
                <div className="flex flex-wrap items-center gap-1.5">
                  {product.categories && product.categories[0] && <Badge variant="secondary" className="text-xs font-normal">
                      {product.categories[0].name}
                    </Badge>}
                  {product.tags && product.tags[0] && <Badge variant="outline" className="text-xs font-normal">
                      {product.tags[0].name}
                    </Badge>}
                </div>
                <CardTitle className="text-base font-semibold leading-snug">
                  <Link to={product.link || "/ProductsPage"} className="hover:text-primary transition-colors">
                    {product.title}
                  </Link>
                </CardTitle>
                {product.short_description ? <div className="line-clamp-2 text-xs text-muted-foreground [&_p]:m-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : null}
              </CardHeader>
            </div>

            <CardContent className="pt-0">
              <div className="mt-2">
                <ProductPrice product={product} className="text-sm font-medium" />
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2 pt-2 border-t border-border">
              <div className="w-full">
                <AddToCartButton product={product} productId={product.id} variant="default" size="sm" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full" text="Add to Cart" showQuantity={false} />
              </div>
              <Button variant="ghost" size="sm" asChild className="w-full justify-between text-xs">
                <Link to={product.link || "/ProductsPage"}>
                  <span>View Details</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </CardFooter>
          </Card>)}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {staticProducts.map((item, i) => <Card key={item.id} data-index={i} className="flex flex-col justify-between border border-border bg-card shadow-xs transition-shadow hover:shadow-md">
          <div>
            <div className="aspect-4/3 w-full overflow-hidden rounded-t-xl bg-muted">
              <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.title} className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" loading="lazy" />
            </div>

            <CardHeader className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="secondary" className="text-xs font-normal">
                  {item.category}
                </Badge>
                <Badge variant="outline" className="text-xs font-normal">
                  {item.spec}
                </Badge>
              </div>
              <CardTitle className="text-base font-semibold leading-snug">
                <Link to={item.link} className="hover:text-primary transition-colors">
                  {item.title}
                </Link>
              </CardTitle>
              <CardDescription className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                {item.description}
              </CardDescription>
            </CardHeader>
          </div>

          <CardFooter className="pt-2 border-t border-border">
            <Button variant="outline" size="sm" asChild className="w-full justify-between text-xs">
              <Link to={item.link}>
                <span>View Details</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function Catalog() {
  return <section id="productcataloggrid" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <div className="mb-10 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-2">
              Instrumentation Catalog
            </Badge>
            <h2 className="font-default text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              Analytical Equipment & Support Systems
            </h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              Explore specialized Raman analyzers, trace narcotics identification kits, hardware interfaces, and lifecycle service options.
            </p>
          </div>
          <div>
            <Button variant="outline" asChild size="sm">
              <Link to="/ProductsPage" className="inline-flex items-center gap-1.5">
                <span>All Products</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "asc",
        orderby: "menu_order"
      }}>
          <CatalogGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}